import exifr from "exifr";
import type { CameraOriginalEvidence, ContainerEvidence, MetadataField, PhotoRecord } from "./types";

function metadataText(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Date) return Number.isNaN(value.valueOf()) ? String(value) : value.toISOString();
  if (value instanceof Uint8Array) return `<binary ${value.byteLength.toLocaleString()} bytes>`;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_key, child: unknown) => {
      if (child instanceof Date) return child.toISOString();
      if (child instanceof Uint8Array) return `<binary ${child.byteLength.toLocaleString()} bytes>`;
      if (child && typeof child === "object") {
        if (seen.has(child)) return "<circular>";
        seen.add(child);
      }
      return child;
    });
  } catch {
    return String(value);
  }
}

export async function readFullMetadata(file: File): Promise<MetadataField[]> {
  const parsed = await exifr.parse(file, {
    tiff: true, exif: true, gps: true, xmp: true, icc: true, iptc: true, jfif: true, ihdr: true,
    makerNote: true, userComment: true, interop: true, multiSegment: true,
    translateValues: true, reviveValues: false, mergeOutput: false,
  }) as Record<string, Record<string, unknown> | undefined> | undefined;
  if (!parsed) return [];
  const fields: MetadataField[] = [];
  for (const [section, values] of Object.entries(parsed)) {
    if (!values || typeof values !== "object") continue;
    if (values instanceof Uint8Array) {
      fields.push({ section, name: "Raw bytes", value: metadataText(values) });
      continue;
    }
    for (const [name, value] of Object.entries(values)) {
      fields.push({ section, name, value: metadataText(value) });
    }
  }
  return fields.sort((left, right) => left.section.localeCompare(right.section) || left.name.localeCompare(right.name));
}

function metadataNumber(metadata: MetadataField[], name: string): number | undefined {
  const field = metadata.find((item) => item.name === name);
  if (!field) return undefined;
  const value = Number(field.value);
  return Number.isFinite(value) ? value : undefined;
}

function metadataOrientation(metadata: MetadataField[]): number | undefined {
  const value = metadata.find((item) => item.name === "Orientation")?.value;
  if (!value) return undefined;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const labels: Record<string, number> = {
    "Horizontal (normal)": 1, "Mirror horizontal": 2, "Rotate 180": 3, "Mirror vertical": 4,
    "Mirror horizontal and rotate 270 CW": 5, "Rotate 90 CW": 6,
    "Mirror horizontal and rotate 90 CW": 7, "Rotate 270 CW": 8,
  };
  return labels[value];
}

function containsAscii(bytes: Uint8Array, text: string): boolean {
  const needle = new TextEncoder().encode(text);
  outer: for (let index = 0; index <= bytes.length - needle.length; index += 1) {
    for (let offset = 0; offset < needle.length; offset += 1) if (bytes[index + offset] !== needle[offset]) continue outer;
    return true;
  }
  return false;
}

export async function inspectContainer(file: File, metadata: MetadataField[] = []): Promise<ContainerEvidence> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const extension = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  const jpeg = bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8;
  const evidence: ContainerEvidence = {
    format: jpeg ? "JPEG/MPO" : file.type || extension.toUpperCase() || "Unknown",
    mimeType: file.type || "unknown",
    extension,
    orientation: metadataOrientation(metadata),
  };
  if (!jpeg) return evidence;

  let lastEoi = -1;
  let soi = 0;
  let eoi = 0;
  for (let index = 0; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0xff && bytes[index + 1] === 0xd8) soi += 1;
    if (bytes[index] === 0xff && bytes[index + 1] === 0xd9) { eoi += 1; lastEoi = index; }
  }
  evidence.soiMarkers = soi;
  evidence.eoiMarkers = eoi;
  evidence.jpegPayloadBytes = lastEoi >= 0 ? lastEoi + 2 : bytes.length;
  evidence.trailingBytes = Math.max(0, bytes.length - (evidence.jpegPayloadBytes ?? bytes.length));
  let zeroes = 0;
  for (let index = bytes.length - 1; index >= 0 && bytes[index] === 0; index -= 1) zeroes += 1;
  evidence.trailingZeroBytes = zeroes;

  const metadataBytes = bytes.subarray(0, Math.min(bytes.length, 2_000_000));
  evidence.hasMpf = containsAscii(metadataBytes, "MPF\0");
  evidence.format = evidence.hasMpf ? "JPEG/MPO" : "JPEG";
  evidence.hasIcc = containsAscii(metadataBytes, "ICC_PROFILE\0");
  evidence.hasExif = containsAscii(metadataBytes, "Exif\0\0");
  evidence.hasXmp = containsAscii(metadataBytes, "http://ns.adobe.com/xap/1.0/");

  let offset = 2;
  while (offset + 4 < bytes.length && bytes[offset] === 0xff) {
    const marker = bytes[offset + 1];
    if (marker === 0xda || marker === 0xd9) break;
    if (marker === 0x00 || marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7)) { offset += 2; continue; }
    const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (length < 2 || offset + 2 + length > bytes.length) break;
    const segment = new TextDecoder("latin1").decode(bytes.slice(offset + 4, Math.min(offset + 80, offset + 2 + length)));
    if (marker === 0xe1 && segment.startsWith("Exif\0\0")) evidence.exifSegmentBytes = (evidence.exifSegmentBytes ?? 0) + length + 2;
    if (marker === 0xe1 && segment.startsWith("http://ns.adobe.com/xap/1.0/")) evidence.xmpSegmentBytes = (evidence.xmpSegmentBytes ?? 0) + length + 2;
    if (marker === 0xe2 && segment.startsWith("ICC_PROFILE\0")) evidence.iccSegmentBytes = (evidence.iccSegmentBytes ?? 0) + length + 2;
    if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker) && length >= 7) {
      evidence.storedHeight = (bytes[offset + 5] << 8) | bytes[offset + 6];
      evidence.storedWidth = (bytes[offset + 7] << 8) | bytes[offset + 8];
      evidence.progressiveJpeg = marker === 0xc2;
    }
    offset += 2 + length;
  }
  evidence.storedWidth ??= metadataNumber(metadata, "ExifImageWidth");
  evidence.storedHeight ??= metadataNumber(metadata, "ExifImageHeight");
  return evidence;
}

function cameraFilename(name: string): boolean {
  return /^(IMG|DSC|DSCF|PXL|MVIMG|SAM|DCIM)[-_ ]?\d+/i.test(name);
}

export function assessCameraOriginal(photo: PhotoRecord, peers: PhotoRecord[]): CameraOriginalEvidence {
  let score = 0;
  const reasons: string[] = [];
  const warnings: string[] = [];
  const edited = Boolean(photo.software && /photoshop|instagram|whatsapp|facebook|messenger|snapseed|export|lightroom/i.test(photo.software));
  if (photo.camera) { score += 2; reasons.push("camera make/model retained"); }
  if (photo.takenAt) { score += 2; reasons.push("original capture time retained"); }
  if (cameraFilename(photo.name)) { score += 1; reasons.push("camera-style filename"); }
  if (photo.software && !edited) { score += 1; reasons.push("camera software tag retained"); }
  if ((photo.metadata?.length ?? 0) >= 20) { score += 1; reasons.push(`${photo.metadata!.length} metadata fields retained`); }
  if (photo.container?.hasExif) { score += 1; reasons.push("EXIF container present"); }
  if (photo.container?.hasMpf) { score += 1; reasons.push("MPO/auxiliary image structure retained"); }
  if (photo.container?.orientation && photo.container.orientation !== 1) { score += 1; reasons.push("camera orientation tag retained"); }
  if (edited) { score -= 4; warnings.push(`editing/export software detected: ${photo.software}`); }
  if ((photo.container?.trailingBytes ?? 0) > 0) warnings.push(`${photo.container!.trailingBytes!.toLocaleString()} trailing bytes after JPEG payload`);

  const peerKeepsOrientation = peers.some((peer) => peer.id !== photo.id && (peer.container?.orientation ?? 1) !== 1);
  if ((photo.container?.orientation ?? 1) === 1 && peerKeepsOrientation) {
    score -= 2;
    warnings.push("pixels appear orientation-normalized while another copy retains the camera orientation tag");
  }
  const verdict: CameraOriginalEvidence["verdict"] = edited || ((photo.container?.orientation ?? 1) === 1 && peerKeepsOrientation)
    ? "possible-processed-copy"
    : score >= 8 ? "likely-camera-original" : score >= 5 ? "possible-camera-original" : "unknown";
  return { score, verdict, reasons, warnings };
}
